import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CookieService } from '@core/services/cookie.service';
import { DjangoService } from '@core/services/django.service';
import { LOCALSTORAGE } from '@core/services/local-storage.service';
import { ACCESS_EXPIRY_KEY, SESSION_HINT_COOKIE } from '@core/utils/constants';
import {
    BehaviorSubject,
    defer,
    EMPTY,
    firstValueFrom,
    Observable,
    Subject,
    timer,
} from 'rxjs';
import { catchError, concatWith, map, share, switchMap } from 'rxjs/operators';

// Renew ahead of the expiry so a request already in flight cannot lose the race.
const RENEW_AHEAD_SECONDS = 30;

// Held for the whole of a renewal, so tabs take their turn at it.
const RENEWAL_LOCK = 'fa-session-renewal';

// Stands in for an expiry a tab never heard, so it renews at once.
const UNKNOWN_EXPIRY = new Date(0).toISOString();

// Where the session lived before it moved into HttpOnly cookies.
const SUPERSEDED_TOKEN_KEYS = ['JWT_TOKEN', 'REFRESH_TOKEN'];

/**
 * Owns the browser's side of the session: whether one may exist, and keeping it
 * alive until it does not.
 *
 * The tokens are HttpOnly, so nothing here can read them. What the server does
 * tell us is when the access cookie expires, which is enough to renew ahead of
 * it rather than waiting for a request to be rejected — that costs three
 * round-trips where one would do. That expiry is kept in localStorage, where it
 * survives a reload and the other tabs on the same cookies can read it. It says
 * only when to ask: the server stays the only authority on whether a token is
 * live, and a stored value that has drifted costs one request or one 401.
 */
@Injectable({
    providedIn: 'root',
})
export class SessionService {
    protected cookieService = inject(CookieService);
    protected djangoService = inject(DjangoService);
    private localStorage = inject(LOCALSTORAGE);

    /** The expiry the server last stated, or null once there is no session. */
    private readonly accessExpiry = new Subject<string | null>();

    private readonly changed = new BehaviorSubject<void>(undefined);

    /**
     * Whether a session may exist, re-read whenever that could have changed.
     *
     * Only the server can answer it for certain. This says whether asking is
     * worth a request, so an anonymous visitor costs none.
     */
    readonly mayExist: Observable<boolean> = this.changed.pipe(
        map(() => !!this.cookieService.getCookie(SESSION_HINT_COOKIE))
    );

    /** The new expiry. Callers arriving mid-flight share the request in flight. */
    private readonly renewal: Observable<string> = defer(() => {
        // Read before queueing, since what marks a stored expiry as another
        // tab's is that it changed while this one waited.
        const beforeQueueing = this.localStorage.getItem(ACCESS_EXPIRY_KEY);
        return navigator.locks.request(RENEWAL_LOCK, () =>
            this.renewUnlessOvertaken(beforeQueueing)
        );
    }).pipe(
        share({
            resetOnComplete: true,
            resetOnError: true,
            // A renewal outlives the request that asked for it, which may be
            // cancelled with the component that made it.
            resetOnRefCountZero: false,
        })
    );

    /** Shared so a burst of rejected requests spends one logout between them. */
    private readonly sharedLogout: Observable<void> = this.djangoService
        .logout()
        .pipe(
            map(() => {
                this.localStorage.removeItem(ACCESS_EXPIRY_KEY);
                this.accessExpiry.next(null);
                this.announceChange();
                return undefined;
            }),
            // A logout outlives the component that asked for it.
            share({ resetOnRefCountZero: false })
        );

    constructor() {
        this.discardSupersededTokens();
        this.keepAlive();
    }

    /** Begins the renewal cycle from a login or registration response. */
    start(accessExpiration: string): void {
        this.localStorage.setItem(ACCESS_EXPIRY_KEY, accessExpiration);
        this.accessExpiry.next(accessExpiration);
        this.announceChange();
    }

    /**
     * Logs out. The local state follows the server's response rather than
     * leading it: announcing first re-reads a hint that still says a session may
     * exist, which asks for the user again, without end.
     */
    logout(): Observable<void> {
        return this.sharedLogout;
    }

    renew(): Observable<void> {
        return this.renewal.pipe(
            map(expiry => {
                this.accessExpiry.next(expiry);
                return undefined;
            })
        );
    }

    /**
     * Renews shortly before each stated expiry, for as long as there is one.
     *
     * Every renewal states the next expiry, so the cycle sustains itself from the
     * one the login response gave it. A newer expiry replaces the renewal waiting
     * on the old one, and null ends it, so there is no timer to cancel by hand.
     *
     * Overridden while prerendering, where there is no session to keep, which
     * keeps the arming below out of the prerender's way too.
     */
    protected keepAlive(): void {
        this.accessExpiry
            .pipe(
                switchMap(expiry => {
                    const renewIn = renewAt(expiry);
                    return renewIn === null ? EMPTY : timer(renewIn);
                }),
                switchMap(() => this.renew()),
                catchError((error: HttpErrorResponse, cycle) => {
                    // Only a rejected refresh cookie means the session is over. A
                    // 500 or a dropped connection leaves the cycle waiting, and
                    // the next request finds out for itself.
                    if (error.status !== 401) {
                        return cycle;
                    }
                    return this.logout().pipe(
                        catchError(() => EMPTY),
                        concatWith(cycle)
                    );
                })
            )
            .subscribe();
        this.resume();
    }

    /**
     * Arms the cycle in a tab that loaded into a session already under way.
     *
     * Nothing else arms one: it has no login response, only cookies. A tab that
     * finds no stored expiry cannot tell how much is left, so it renews now.
     */
    private resume(): void {
        if (!this.cookieService.getCookie(SESSION_HINT_COOKIE)) {
            return;
        }
        this.accessExpiry.next(
            this.localStorage.getItem(ACCESS_EXPIRY_KEY) ?? UNKNOWN_EXPIRY
        );
    }

    /**
     * Renews, unless another tab did so while this one waited for the lock.
     *
     * Only a changed expiry says that. One that merely looks live is as likely
     * to be this tab's own, and skipping on that skips every renewal from here on.
     */
    private async renewUnlessOvertaken(
        beforeQueueing: string | null
    ): Promise<string> {
        const stored = this.localStorage.getItem(ACCESS_EXPIRY_KEY);
        if (stored !== null && stored !== beforeQueueing) {
            return stored;
        }
        const { access_expiration } = await firstValueFrom(
            this.djangoService.refresh()
        );
        // Stored before the lock releases, so the tab next in line reads it. Firefox
        // hands a write to other tabs only once the task ends, so the lock waits one.
        this.localStorage.setItem(ACCESS_EXPIRY_KEY, access_expiration);
        await firstValueFrom(timer(0));
        return access_expiration;
    }

    /**
     * Removes the session tokens from where they lived before the cookies.
     *
     * The refresh endpoint still accepts one in a request body, so a leftover is
     * a readable credential until it expires. Deletable once no browser is
     * loading a bundle from before this shipped.
     */
    private discardSupersededTokens(): void {
        for (const key of SUPERSEDED_TOKEN_KEYS) {
            this.localStorage.removeItem(key);
        }
    }

    /** The cached user belongs to the session that just changed. */
    private announceChange(): void {
        this.djangoService.clear_cache();
        this.changed.next();
    }
}

/** How long to wait before renewing, or null when there is nothing to wait for. */
function renewAt(accessExpiration: string | null): number | null {
    if (accessExpiration === null) {
        return null;
    }
    const renewIn =
        new Date(accessExpiration).getTime() -
        Date.now() -
        RENEW_AHEAD_SECONDS * 1000;
    // An expiry that will not parse gives NaN, which a timer treats as zero and
    // would renew in a loop. An expiry already past is negative, and renewing
    // straight away is right.
    return Number.isFinite(renewIn) ? renewIn : null;
}
